import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelBed } from '@withjoy/joykit/icons';
import { Flex, Box, TextV2 } from '@withjoy/joykit';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { MapPinWithLabel } from '@shared/components/JoyInteractableMap/MapPin';
import { DirectionsRenderer } from '@react-google-maps/api';
import { JoyInteractableMap, MAP_CARDS_PORTAL_ID, VenueMapPin } from '@shared/components/JoyInteractableMap';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { withDirectionsResult } from './withDirectionsResult';
import { AccommodationCombinedType } from '@apps/thanksForRsvp/utils/AccommodationCard.utils';
import { AccommodationCombined } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/Accommodations/Accommodation.types';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useAccommodationsCta } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/hooks/useAccommodationsCta';
import { TravelMetricsOverlay } from './components/TravelMetricsOverlay';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useTranslation } from '@shared/core';

export interface DirectionsMapProps
  extends Readonly<{
    destination: { lat: number; lng: number; photo?: string; title?: string };
    originList: { id: string; index?: number; lat: number; lng: number; hotel?: AccommodationCombined }[];
    locationBeingHovered?: string;
    gestureHandling?: 'cooperative' | 'greedy' | 'none';
    hideDirection?: boolean;
    onMapClick?: () => void;
    onOriginClick?: (id: string, ctaUrl?: string) => void;
    isAnimate?: boolean;
    showTravelMetric?: boolean;
  }> {}

export interface PricingComponentProps
  extends Readonly<{
    hotel: Readonly<AccommodationCombined>;
    setIsPriceAvailable: (isPriceAvailable: boolean) => void;
  }> {}

interface CircleMapPinComponentProps
  extends Readonly<{
    index: number;
    isAnimate?: boolean;
    showTravelMetric?: boolean;
    locationBeingHovered?: string;
    onOriginClick?: (id: string, ctaUrl?: string) => void;
    origin: Readonly<{ id: string; lat: number; lng: number; index?: number; hotel?: AccommodationCombined }>;
  }> {}

const CircleMapPinComponent = ({ origin, locationBeingHovered, onOriginClick, index, isAnimate, showTravelMetric }: CircleMapPinComponentProps) => {
  const { tq } = useTranslation('sharedComponents');
  const showRegularHotelPricing = !origin.hotel?.customUrl && origin.hotel?.isFromAccommodationCatalog;
  const isHighlighted = origin.id === locationBeingHovered;

  const isShowPricing = origin.hotel?.type === AccommodationCombinedType.Hotel && showRegularHotelPricing;
  const { ctaUrl, handleOnSelectAccommodation } = useAccommodationsCta({
    accommodation: origin.hotel,
    searchParams: {
      utm_medium: 'web',
      utm_source: 'website_accommodations_page',
      utm_campaign: 'directions_map',
    },
  });

  const { price: accommodationPrice, strikeOutPrice } = useTravelPlacesPricingAndBookingDetailsFragment(origin.hotel?.id, 'accommodationPlace', !isShowPricing);

  const isGreenBackground =
    (origin.hotel?.type === AccommodationCombinedType.Hotel || (origin.hotel?.type === AccommodationCombinedType.Roomblock && origin.hotel?.isJoyManagedRoomBlock)) &&
    accommodationPrice;

  const handleMapPointClick = () => {
    onOriginClick?.(origin.id, ctaUrl);
    handleOnSelectAccommodation();
  };

  return (
    <MapPinWithLabel
      key={origin.id}
      position={{ lat: origin.lat, lng: origin.lng }}
      isActive={isHighlighted}
      showOnTop={isHighlighted}
      onClick={handleMapPointClick}
      onDoubleClick={handleMapPointClick}
      image={origin.hotel?.photo?.url ? `url(${origin.hotel.photo.url})` : ''}
      isLargeImage={isAnimate}
      isAnimate={isAnimate}
      style={{
        animationDelay: `${index * 700}ms`,
      }}
      label={
        <Flex
          justifyContent="center"
          alignItems="center"
          gap={2}
          position="relative"
        >
          {isAnimate ? (
            <HotelBed />
          ) : (
            <Flex
              backgroundColor={isGreenBackground ? 'accentGreen3' : 'mono8'}
              borderRadius={'full'}
              justifyContent="center"
              alignItems="center"
              height={pxToRem(24)}
              width={pxToRem(24)}
            >
              <TextV2
                typographyVariant="label3"
                color={'white'}
                fontSize={pxToRem(11)}
                fontWeight={600}
                fontFamily="Inter UI"
              >
                {showTravelMetric ? tq`H` : index}
              </TextV2>
            </Flex>
          )}

          {isAnimate && origin.hotel?.type === AccommodationCombinedType.Hotel && strikeOutPrice && (
            <TextV2
              typographyVariant="label3"
              fontSize={pxToRem(12)}
              fontWeight={500}
              fontFamily="Inter UI"
              textDecoration="line-through"
            >
              {strikeOutPrice}
            </TextV2>
          )}

          {origin.hotel?.type === AccommodationCombinedType.Roomblock && !!origin.hotel?.strikeoutPricePerNight && (
            <TextV2
              typographyVariant="label3"
              fontSize={pxToRem(13)}
              fontWeight={600}
              fontFamily="Inter UI"
            >
              ${origin.hotel?.strikeoutPricePerNight?.toString()}
            </TextV2>
          )}
          {accommodationPrice && (
            <TextV2
              fontSize={pxToRem(13)}
              fontWeight={600}
            >
              {accommodationPrice}
            </TextV2>
          )}
        </Flex>
      }
    />
  );
};

const DirectionsMapComponent: React.FC<DirectionsMapProps & { directions: google.maps.DirectionsResult | null; loading: boolean }> = ({
  directions,
  destination,
  originList,
  locationBeingHovered,
  gestureHandling,
  isAnimate,
  hideDirection: hideDirectionProps,
  onMapClick,
  onOriginClick,
  children,
  showTravelMetric = false,
}) => {
  const { getCalculatedCenterAndZoom } = useGoogleInteractableMap();
  const hasDestination = destination.lat !== 0 && destination.lng !== 0;
  const hideDirection = !hasDestination || hideDirectionProps;

  const markers = originList
    .map(origin => ({ lat: origin.lat, lng: origin.lng }))
    .concat(destination)
    .filter(marker => marker.lat !== 0 || marker.lng !== 0);

  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));

  const distance = useMemo(() => {
    if (originList.length > 0) {
      return distanceInMiles(originList[0], destination);
    }
    return null;
  }, [originList, destination]);

  if (!getCalculatedCenterAndZoom || markers.length === 0) {
    return null;
  }

  const { center, bounds } = getCalculatedCenterAndZoom(markers);

  return (
    <JoyInteractableMap
      center={center}
      {...(markers.length > 1 && { bounds: bounds })}
      zoom={13}
      autoCenterAndZoom={markers.length > 0}
      gestureHandling={gestureHandling}
      onClick={onMapClick}
    >
      {children}
      {!hideDirection && directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#494DCB',
              strokeWeight: 3,
            },
          }}
        />
      )}
      {hasDestination && <VenueMapPin position={destination} />}

      {showTravelMetric && distance && (
        <Flex
          left={0}
          zIndex={1}
          width="100%"
          bottom={pxToRem(12)}
          position="absolute"
          justifyContent="center"
        >
          <TravelMetricsOverlay distance={distance} />
        </Flex>
      )}

      {isMobile && (
        <Box
          id={MAP_CARDS_PORTAL_ID}
          position="fixed"
          width={`calc(100% - ${pxToRem(32)})`}
          padding={5}
          bottom={0}
          left={0}
        />
      )}
      {originList.map((origin, index) => (
        <CircleMapPinComponent
          key={origin.id}
          origin={origin}
          index={origin.index ? origin.index + 1 : index + 1}
          locationBeingHovered={locationBeingHovered}
          onOriginClick={onOriginClick}
          isAnimate={isAnimate}
          showTravelMetric={showTravelMetric}
        />
      ))}
    </JoyInteractableMap>
  );
};

const DirectionsMap = withDirectionsResult(DirectionsMapComponent);

DirectionsMap.displayName = 'DirectionsMap';

export { DirectionsMap };
